import React, { useState, useEffect } from 'react';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableSortLabel,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import FormHelperText from '@material-ui/core/FormHelperText';
import moment from 'moment';
import { EditIcon } from 'components/icons';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import {
  CurrencyInputWrapper,
  FormActionButtons,
  PopoverWrapper,
  CustomDateRangePicker,
  Input,
  AppHeader,
  CircularLoader
} from 'components';
import useStyles from './styles';
import {
  CREATE_RATE_PLAN,
  DELETE_RATE_PLAN
} from 'graphql/mutations/accommodation';
import { GET_RATE_PLANS } from 'graphql/queries/accommodations';

const initialValues = {
  USD: '0',
  EUR: '0',
  NOK: '0',
  dateRange: null,
  ratePlanes: []
};

const RatePlan = ({
  handleSubmit,
  setSubmitButton,
  isUpdate,
  anyTouched,
  error,
  dateRange,
  USD,
  EUR,
  NOK,
  ratePlanes,
  change,
  businessOwner
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [rows, setRows] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const { enqueueSnackbar } = useSnackbar();

  // GraphQL operations
  const { loading, data, refetch } = useQuery(GET_RATE_PLANS, {
    variables: {
      businessOwnerId:
        businessOwner.selectedBusinessOwner.ownerId ||
        businessOwner.selectedBusinessOwner.id
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });
  const [createRatePlan, { loading: creating }] = useMutation(CREATE_RATE_PLAN);
  const [deleteRatePlan, { loading: deleting }] = useMutation(DELETE_RATE_PLAN);

  // Load existing rate plans on mount
  useEffect(() => {
    if (data?.ratePlans) {
      const formattedRows = data.ratePlans.map(plan => ({
        id: plan.id,
        ratePlanes: [plan.title],
        startDate: plan.prices[0]?.startDate || '',
        endDate: plan.prices[0]?.endDate || '',
        price: plan.prices.find(p => p.currency.currency === 'USD')?.value || 0,
        EUR: plan.prices.find(p => p.currency.currency === 'EUR')?.value || 0,
        NOK: plan.prices.find(p => p.currency.currency === 'NOK')?.value || 0
      }));
      setRows(formattedRows);
    }
  }, [data]);

   React.useLayoutEffect(() => {
     const updateSize = () => {
       setWindowWidth(window.innerWidth);
     };
     window.addEventListener('resize', updateSize);
     updateSize();
     return () => window.removeEventListener('resize', updateSize);
   }, []);

  const handleAddPrice = () => {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
       enqueueSnackbar('Please select a date range', { variant: 'warning' });
      return;
    }

    const newEntry = {
      startDate: moment(dateRange.startDate).format('MMMM-DD YYYY'),
      endDate: moment(dateRange.endDate).format('MMMM-DD YYYY'),
      price: parseFloat(USD) || 0,
      EUR: parseFloat(EUR) || 0,
      NOK: parseFloat(NOK) || 0,
      ratePlanes: Array.isArray(ratePlanes)
        ? ratePlanes
        : [ratePlanes].filter(Boolean)
    };

    setRows(prevRows => [...prevRows, newEntry]);
    resetFormFields();
  };

  const formatDateForAPI = dateString => {
    // Convert "April-30 2025" to "2025-04-30"
    return moment(dateString, 'MMMM-DD YYYY').format('YYYY-MM-DD');
  };

  const handleSubmitForm = async () => {
    try {
      // Get all rows that haven't been saved yet (those without IDs)
      const unsavedRows = rows.filter(row => !row.id);

      // Process each unsaved row
      for (const row of unsavedRows) {
        // Create the prices array for this row
        const formattedPrices = [
          {
            currency: 'USD',
            value: row.price,
            startDate: formatDateForAPI(row.startDate),
            endDate: formatDateForAPI(row.endDate)
          },
          {
            currency: 'EUR',
            value: row.EUR,
            startDate: formatDateForAPI(row.startDate),
            endDate: formatDateForAPI(row.endDate)
          },
          {
            currency: 'NOK',
            value: row.NOK,
            startDate: formatDateForAPI(row.startDate),
            endDate: formatDateForAPI(row.endDate)
          }
        ].filter(price => price.value > 0);

        await createRatePlan({
          variables: {
            title: row.ratePlanes[0] || 'Untitled Rate Plan',
            prices: formattedPrices,
            businessOwnerId: businessOwner.selectedBusinessOwner.id
          }
        }).then(
          () => {
            enqueueSnackbar(t('Rate plan has been successfully created.'), {
              variant: 'success'
            });
          },
          () => {
            enqueueSnackbar(t('Something was wrong'), {
              variant: 'error'
            });
            setIsLoading(false);
          }
        );
      }

      // Refresh the data after successful creation
      refetch();
      resetFormFields();
    } catch (err) {
      console.error('Error creating rate plan:', err);
    }
  };

  const handleDeleteConfirmed = async () => {
    try {
      await deleteRatePlan({
        variables: {
          id: confirmDelete,
          businessOwnerId: businessOwner.selectedBusinessOwner.id
        }
      });
      refetch();
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting rate plan:', err);
    }
  };

  const resetFormFields = () => {
    change('ratePlanes', []);
    change('dateRange', null);
    change('USD', '0');
    change('EUR', '0');
    change('NOK', '0');
  };

  const handleSort = column => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleDeleteRow = index => {
    const row = rows[index];
    if (row.id) {
      setConfirmDelete(row.id);
    } else {
      setRows(prevRows => prevRows.filter((_, i) => i !== index));
    }
  };

  const handleEditRow = index => {
    const row = rows[index];
    change('dateRange', {
      startDate: moment(row.startDate).toDate(),
      endDate: moment(row.endDate).toDate()
    });
    change('USD', row.price.toString());
    change('EUR', row.EUR.toString());
    change('NOK', row.NOK.toString());
    handleDeleteRow(index);
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (orderBy) {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
    }
    return 0;
  });


  return (
    <>
      <CircularLoader
        isLoading={loading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      <AppHeader />
      <div className={classes.root}>
        <div className={classes.inputClassName}>
          <Field
            name="ratePlanes"
            component={Input}
            label="Rate Plan"
            placeholder="Add Your Rate Plan Title"
            className={classes.chipsInput}
          />
        </div>

        <div className={classes.datePickerWrapper}>
          <Field
            name="dateRange"
            label="Select Dates For Rate Plan"
            inputClassName={classes.inputClassName}
            className={classes.datePickerWrapper}
            component={CustomDateRangePicker}
            numberOfMonths={
              windowWidth > 1010 ? 2 : 1 || windowWidth <= 750 ? 1 : 2
            }
            daySize={windowWidth > 600 ? 38 : 41}
            RatePlan
          />
        </div>

        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className={classes.currencyRow}>
            <div className={classes.row}>
              <Field
                name="NOK"
                label="NOK"
                isHiddenErrorMsg={true}
                className={classes.currencyField}
                component={CurrencyInputWrapper}
              />
              <Field
                name="EUR"
                label="EUR"
                isHiddenErrorMsg={true}
                className={classes.currencyField}
                component={CurrencyInputWrapper}
              />
              <Field
                name="USD"
                label="USD"
                isHiddenErrorMsg={true}
                className={classes.currencyField}
                component={CurrencyInputWrapper}
              />
              <PopoverWrapper text="Value for at least one currency for each category should be filled in. Currencies marked with zero (or empty) will not be displayed on the widget.">
                <HelpIcon className={classes.helpIcon} />
              </PopoverWrapper>
            </div>
            {anyTouched && error && (
              <FormHelperText error>{t(error)}</FormHelperText>
            )}
            <Button
              className={classes.addEntry}
              variant="contained"
              color="primary"
              type="button"
              onClick={handleAddPrice}
            >
              Add Prices
            </Button>
          </div>

          <TableContainer component={Paper} className={classes.table}>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    'ratePlanes',
                    'startDate',
                    'endDate',
                    'price',
                    'EUR',
                    'NOK'
                  ].map(column => (
                    <TableCell key={column}>
                      <TableSortLabel
                        active={orderBy === column}
                        direction={order}
                        onClick={() => handleSort(column)}
                      >
                        {column === 'price'
                          ? 'Price (USD)'
                          : column === 'ratePlanes'
                          ? 'Rate Plans'
                          : column.replace(/startDate|endDate/, match =>
                              match === 'startDate' ? 'Start Date' : 'End Date'
                            )}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row, index) => (
                  <TableRow key={row.id || index}>
                    <TableCell>
                      {Array.isArray(row.ratePlanes)
                        ? row.ratePlanes.join(', ')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{row.startDate}</TableCell>
                    <TableCell>{row.endDate}</TableCell>
                    <TableCell>{row.price}</TableCell>
                    <TableCell>{row.EUR}</TableCell>
                    <TableCell>{row.NOK}</TableCell>
                    <TableCell className={classes.tableCell}>
                      <div
                        style={{ display: 'inline-flex', paddingRight: '10px' }}
                      >
                        {/* <IconButton
                          className={classes.iconButton}
                          onClick={() => handleEditRow(index)}
                          aria-label="edit"
                        >
                          <EditIcon className={classes.editIcon} />
                        </IconButton> */}
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => handleDeleteRow(index)}
                          aria-label="delete"
                        >
                          <HighlightOffIcon className={classes.closeIcon} />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <FormActionButtons
            cancelPath="/accommodations-and-activities"
            actionLabel={'Save'}
            setSubmitButton={setSubmitButton}
            isUpdate={isUpdate}
            disabled={creating}
          />
        </form>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this rate plan?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirmed}
            color="secondary"
            disabled={deleting}
          >
            {deleting ? <CircularLoader size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const selector = formValueSelector('RatePlan');

const mapStateToProps = state => ({
  RatePlan: state.form.RatePlan,
  dateRange: selector(state, 'dateRange'),
  USD: selector(state, 'USD'),
  EUR: selector(state, 'EUR'),
  NOK: selector(state, 'NOK'),
  ratePlanes: selector(state, 'ratePlanes'),
  businessOwner: state.businessOwner
});

export default reduxForm({
  form: 'RatePlan',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  initialValues
})(connect(mapStateToProps, null)(RatePlan));
